import { updateWorkbook } from "@/apis/workbook/route";
import { DsdFile } from "@/types/dsd";
import { ModalWrapper } from "@/components/modals/modal-wrapper";
import queries from "@/queries";
import { Button } from "@/toggle-ui-kit/button";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";

export const RenameDsdModal = ({
  file,
  onClose,
}: {
  file: DsdFile | null;
  onClose: () => void;
}) => {
  const currentDisplayName = file?.displayName || file?.name || "";
  const [newName, setNewName] = useState<string>(currentDisplayName);
  const queryClient = useQueryClient();

  const { mutate: renameMutation, isPending } = useMutation({
    mutationFn: ({
      workbookId,
      displayName,
    }: {
      workbookId: string;
      displayName: string;
    }) => updateWorkbook(workbookId, displayName),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queries.dsd().queryKey,
      });
      toast.success("보고서 이름이 변경되었습니다.");
      onClose();
    },
    onError: () => {
      toast.error("파일 이름 변경에 실패했습니다.");
    },
  });

  const handleSave = () => {
    if (!file) return;
    if (newName.trim() === "") {
      toast.error("파일 이름을 입력해주세요.");
      return;
    }
    if (newName.trim() === currentDisplayName) {
      toast.error("기존 파일 이름과 동일합니다.");
      return;
    }
    renameMutation({ workbookId: file.id, displayName: newName.trim() });
  };

  return (
    <ModalWrapper className="flex w-[458px] flex-col gap-0 rounded-lg border border-line-normal bg-white p-0 shadow-2xl">
      <div className="flex items-center px-6 pt-5">
        <h2 className="label-1-normal-semibold text-label-normal">
          보고서 이름 변경
        </h2>
      </div>

      <div className="flex flex-col gap-2 px-6 py-4">
        <p className="label-2-medium text-label-alternative">새로운 이름</p>
        <input
          type="text"
          placeholder={currentDisplayName}
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          className="body-1-normal-regular w-full rounded-lg border border-line-normal bg-white px-3 py-2 text-label-normal outline-none placeholder:text-label-assistive focus:shadow-input-focus"
        />
      </div>

      <div className="flex justify-end gap-2 px-6 pb-5">
        <Button
          variant="outlined"
          size="base"
          onClick={onClose}
          disabled={isPending}
          className="min-w-[74px]"
        >
          취소
        </Button>
        <Button
          variant="solid"
          size="base"
          onClick={handleSave}
          disabled={
            newName.trim() === "" ||
            newName.trim() === currentDisplayName ||
            isPending
          }
          className="min-w-[74px]"
        >
          {isPending ? "변경 중..." : "변경"}
        </Button>
      </div>
    </ModalWrapper>
  );
};
